import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  forwardRef,
  useImperativeHandle,
} from 'react';
// ExtraInformation Component
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import styles from './ExtraInformation.module.scss';
import api from '../../../utils/api';
// Note: `api` is kept for the safari enquiry form submission
import { getDestinationsByCountry } from '../../../server/getDestinationsByCountry';
import isEmpty from 'lodash.isempty';
import { DestinationContext } from '../../../contexts/DestinationContext';
import CountrySelect from '../CountrySelect';
import PppnsTooltip from '../PppnsTooltip/PppnsTooltip';
import { Tooltip } from 'react-tooltip';

// Safari idea routes come as either arrow-separated ("Nairobi -> Maasai
// Mara -> Nairobi") or plain hyphen-separated ("Nairobi - Lake Nakuru -
// Maasai Mara - Nairobi") strings — split on either, then dedupe stops
// that repeat (e.g. the return leg back to "Nairobi").
const ROUTE_SEPARATOR = /\s*(?:->|-|→|➜|⇒)\s*/;
const parseRouteNames = (route = '') => {
  if (!route) return [];
  const names = route
    .split(ROUTE_SEPARATOR)
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set(names));
};

const cleanRoute = (route = '') => {
  if (!route) return null;
  const names = parseRouteNames(route);
  return names.length > 0 ? names.join(' -> ') : null;
};

// Budget slider (USD, per person per night) — bounds and the Luxury cutoff
// match the price filter used on the accommodation listing page.
const BUDGET_MIN = 200;
const BUDGET_MAX = 3000;
const BUDGET_STEP = 50;
const BUDGET_DEFAULT = 500;
const LUXURY_THRESHOLD = 650;
const classificationFor = (price) =>
  price > LUXURY_THRESHOLD ? 'Luxury' : 'mid range';

const ExtraInformation = forwardRef(
  (
    {
      displayPage = 'Home',
      destination,
      initialInfo = null,
      initialCountriesList = [],
      howItWorksData = null,
      onOpenPlanner,
      onBrowseCamps,
    },
    ref
  ) => {
    const navigate = useNavigate();
    // Portal root — created on the client only so SSR never touches document.body
    const portalRootRef = useRef(null);
    const [portalMounted, setPortalMounted] = useState(false);

    useEffect(() => {
      const el = document.createElement('div');
      document.body.appendChild(el);
      portalRootRef.current = el;
      setPortalMounted(true);
      return () => {
        document.body.removeChild(el);
      };
    }, []);
    const destinationContext = useContext(DestinationContext);
    // Seeded from SSR loader data; no client-side fetch needed.
    // `howItWorksData` (new destination.howItWorks field) takes priority over the
    // legacy `initialInfo` (fetched extra-information doc, still used on the
    // Accommodation/Safari listing pages) — its description/subtext is plain
    // text rather than the legacy field's rich HTML.
    const [info] = useState(() =>
      howItWorksData
        ? {
            eyebrow: howItWorksData.eyebrow,
            title: howItWorksData.title,
            description: howItWorksData.subtext,
            steps: howItWorksData.steps,
            isPlainText: true,
          }
        : initialInfo
    );
    const [showPlanner, setShowPlanner] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    // The overlay/modal handle their own scroll — without this, the page
    // behind it (home page, safari list, etc.) stays scrollable too, so the
    // background visibly scrolls along with (or independently of) the modal.
    useEffect(() => {
      if (!showPlanner) return undefined;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }, [showPlanner]);

    const contentContainerRef = useRef(null);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [dateTooltip, setDateTooltip] = useState('');

    const [numberOfDays, setNumberOfDays] = useState(null);
    const [adults, setAdults] = useState(2);
    const [childrenCount, setChildrenCount] = useState(0);
    const [infants, setInfants] = useState(0);
    const [travelStyle, setTravelStyle] = useState('Classic Mid-range');
    const [thingsToDo, setThingsToDo] = useState([]);
    const [customThings, setCustomThings] = useState('');
    const [flexible, setFlexible] = useState(null);
    const [flexibleTooltip, setFlexibleTooltip] = useState('');
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [residenceCountry, setResidenceCountry] = useState('');
    const [travelCountry, setTravelCountry] = useState('');
    // Seeded from SSR loader data; no client-side fetch needed
    const [countriesList, setCountriesList] = useState(initialCountriesList);
    const [destinationsList, setDestinationsList] = useState([]);
    const [selectedDestinations, setSelectedDestinations] = useState([]);
    // Set only when the planner was opened from a Safari Ideas card — holds
    // that idea's own duration/title so the trip is labelled e.g. "4 Days /
    // 3 Nights - Classic Mara Explorer" instead of the generic custom-safari
    // label. Cleared the moment the traveller actually edits the trip
    // (destinations or number of days) away from the idea's preset.
    const [ideaInfo, setIdeaInfo] = useState(null);
    // Destination names parsed from an idea's `route` (e.g. "Nairobi ->
    // Maasai Mara -> Nairobi") waiting to be matched against destinationsList
    // once it loads for the prefilled country, then auto-checked.
    const [pendingRouteNames, setPendingRouteNames] = useState([]);
    const [destinationTooltip, setDestinationTooltip] = useState('');
    const [daysTooltip, setDaysTooltip] = useState('');
    const [budgetTooltip, setBudgetTooltip] = useState('');
    const [fullNameTooltip, setFullNameTooltip] = useState('');
    const [emailTooltip, setEmailTooltip] = useState('');
    const [phoneTooltip, setPhoneTooltip] = useState('');
    const [residenceCountryTooltip, setResidenceCountryTooltip] = useState('');
    const [comments, setComments] = useState('');
    const [pricePerPerson, setPricePerPerson] = useState(null);

    // Submission states
    const [loadingCode, setLoadingCode] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // NOTE: extra-information and country data are now fetched server-side in the
    // route loader and passed as initialInfo / initialCountriesList props.
    // The two useEffect fetches that previously ran here have been removed.

    useEffect(() => {
      if (!travelCountry && destinationContext?.country) {
        setTravelCountry(destinationContext.country);
      }
    }, [destinationContext, travelCountry]);

    const [propertyInfo, setPropertyInfo] = useState(null);

    // Lets other homepage sections (hero mini-form, final CTA, booking paths)
    // open this same planner instead of running a parallel enquiry flow.
    useImperativeHandle(ref, () => ({
      open: (prefill = {}) => {
        setStartDate(prefill.startDate || '');
        setDateTooltip('');
        if (prefill.numberOfDays) {
          setNumberOfDays(Number(prefill.numberOfDays));
        } else {
          setNumberOfDays(null);
        }
        if (typeof prefill.adults === 'number') setAdults(prefill.adults);
        if (prefill.email) setEmail(prefill.email);
        setSelectedDestinations(
          prefill.destination?._id ? [prefill.destination] : []
        );
        if (prefill.destination?.country) {
          setTravelCountry(prefill.destination.country);
        }
        // Property card intent (Book Now or Book Direct)
        setPropertyInfo(
          prefill.property
            ? {
                _id: prefill.property._id,
                title: prefill.property.title || prefill.property.nameOverride,
                slug: prefill.property.slug,
                intent: prefill.intent || 'quotes',
                bookDirect: prefill.property.bookDirect,
              }
            : null
        );
        // A safari idea card supplies its own duration + title (+ optional
        // image) — reset to null on every other open (e.g. the generic
        // "Get 3 Free Quotes" CTA) so a previous idea's label/image doesn't
        // leak into a fresh session.
        setIdeaInfo(
          prefill.duration && prefill.title
            ? {
                duration: prefill.duration,
                title: prefill.title,
                image: prefill.image || null,
                route: cleanRoute(prefill.route),
              }
            : null
        );
        setPendingRouteNames(
          prefill.route ? parseRouteNames(prefill.route) : []
        );
        setCurrentStep(0);
        setShowPlanner(true);
      },
    }));

    const checkScrollPosition = () => {
      if (contentContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          contentContainerRef.current;
        setCanScrollDown(scrollHeight - scrollTop - clientHeight > 25);
      }
    };

    // Reset scroll position to top whenever switching steps and re-check scroll indicator
    useEffect(() => {
      if (contentContainerRef.current) {
        contentContainerRef.current.scrollTop = 0;
      }
      const timer = setTimeout(checkScrollPosition, 100);
      return () => clearTimeout(timer);
    }, [currentStep, travelCountry, destinationsList, showPlanner]);

    useEffect(() => {
      if (!travelCountry) {
        setDestinationsList([]);
        return;
      }

      // Runs client-side only (effects never run on the server).
      // Uses createServerFn so the fetch is handled by the server function pipeline.
      getDestinationsByCountry({ data: { countryId: travelCountry } })
        .then((data) => setDestinationsList(data))
        .catch((err) => console.error('Error loading destinations:', err));
    }, [travelCountry]);

    // Once the destination list for the idea's country has loaded, check
    // off whichever ones match the idea's route (e.g. "Nairobi" / "Maasai
    // Mara"). This is programmatic — it must NOT count as the traveller
    // editing their trip, so it doesn't go through handleDestinationToggle
    // and doesn't clear `ideaInfo`.
    useEffect(() => {
      if (pendingRouteNames.length === 0 || destinationsList.length === 0)
        return;

      const orderedMatches = [];
      pendingRouteNames.forEach((name) => {
        const found = destinationsList.find((dest) => {
          const a = name.toLowerCase();
          const b = (dest.name || '').toLowerCase();
          return a && b && (b.includes(a) || a.includes(b));
        });
        if (found && !orderedMatches.some((d) => d._id === found._id)) {
          orderedMatches.push(found);
        }
      });

      if (orderedMatches.length > 0) {
        setSelectedDestinations((prev) => {
          const merged = [...prev];
          orderedMatches.forEach((match) => {
            if (!merged.some((d) => d._id === match._id)) merged.push(match);
          });
          return merged;
        });
      }
      setPendingRouteNames([]);
    }, [pendingRouteNames, destinationsList]);

    const stripParagraphBr = (html) => {
      if (typeof html !== 'string') return html;
      return html.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '');
    };

    const destinationName = destination || destinationContext?.name || '';

    const replaceDestinationName = (text) => {
      if (typeof text !== 'string' || !destinationName) return text;
      return text
        .replace(/Maasai Mara/gi, destinationName)
        .replace(/MaasaiMara/gi, destinationName.replace(/\s+/g, ''));
    };

    // Only the "How it works" content (steps grid + intro copy) depends on
    // `info` being populated — the planner modal itself must stay renderable
    // regardless, since other homepage CTAs (hero, final CTA, booking paths)
    // open it via the imperative `open()` handle independently of this content.
    const hasStepsContent = info && !isEmpty(info.steps);

    const renderSteps = () => (
      <div className={styles['steps-row-wrapper']}>
        <div className={styles['steps-row']}>
          {info.steps.map((step, index) => (
            <div key={index} className={styles['step-card']}>
              <div className={styles['step-badge']}>
                {String(index + 1).padStart(2, '0')}
              </div>
              <div className={styles['step-header']}>
                <h3 className={styles['step-title']}>{step.title}</h3>
              </div>
              {info.isPlainText ? (
                <p className={styles['step-description']}>{step.description}</p>
              ) : (
                <div
                  className={`${styles['step-description']} inserted-html`}
                  dangerouslySetInnerHTML={{
                    __html: step.description,
                  }}
                />
              )}
              {step.statTag && (
                <span className={styles['step-tag']}>{step.statTag}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );

    const renderContent = () => (
      <div className={styles['main-info-content']}>
        {info.eyebrow && (
          <div className={styles['info-eyebrow']}>{info.eyebrow}</div>
        )}
        <h2
          className={styles['info-header']}
          dangerouslySetInnerHTML={{ __html: info.title }}
        />
        {info.isPlainText ? (
          <p className={styles['info-description']}>{info.description}</p>
        ) : (
          <div
            className={`${styles['info-description']} inserted-html`}
            dangerouslySetInnerHTML={{
              __html: info.description,
            }}
          />
        )}
      </div>
    );

    const getThumbnails = () => {
      if (!destinationContext || !destinationContext.images) return [];
      const urls = [];
      const keys = ['home', 'safari', 'accommodation', 'about', 'destination'];
      keys.forEach((key) => {
        const arr = destinationContext.images[key];
        if (Array.isArray(arr)) {
          arr.forEach((img) => {
            if (img && img.url) {
              urls.push(img.url);
            }
          });
        }
      });
      return urls;
    };

    const backupImages = [
      'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=300&q=80',
      'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=300&q=80',
      'https://images.unsplash.com/photo-1523805081326-ed46b7e7bdec?w=300&q=80',
      'https://images.unsplash.com/photo-1456926631375-92c8ce872def?w=300&q=80',
      'https://images.unsplash.com/photo-1504829857797-ddff28127792?w=300&q=80',
    ];

    const thumbnails = [...getThumbnails(), ...backupImages].slice(0, 5);

    // Coming from a safari idea: "4 Days / 3 Nights - Classic Mara
    // Explorer" (the idea's own label). Otherwise: a live "X Days / Y
    // Nights - Custom Safari" label driven by the day count the traveller
    // has entered. Once the traveller edits destinations or days away from
    // the idea's preset, `ideaInfo` is cleared (see handleDestinationToggle
    // and the days input below) and this falls through to Custom Safari.
    const nightsCount = numberOfDays ? Math.max(numberOfDays - 1, 0) : null;
    const safariType = ideaInfo
      ? `${ideaInfo.duration} - ${ideaInfo.title}`
      : numberOfDays
        ? `${numberOfDays} Day${numberOfDays === 1 ? '' : 's'} / ${nightsCount} Night${nightsCount === 1 ? '' : 's'} - Customized Safari`
        : '';

    // Trip Details step (0) preview card — shows the picked safari idea's
    // own thumbnail/title, or, once the traveller starts building their own
    // trip (picking destinations/days clears `ideaInfo`), a live card whose
    // image/title update from `selectedDestinations`/`numberOfDays` instead.
    const previewDuration = ideaInfo
      ? ideaInfo.duration
      : numberOfDays
        ? `${numberOfDays} Day${numberOfDays === 1 ? '' : 's'} / ${nightsCount} Night${nightsCount === 1 ? '' : 's'}`
        : null;
    const previewTitle = ideaInfo ? ideaInfo.title : 'Your Custom Safari';
    const previewImage = ideaInfo?.image || thumbnails[0] || null;
    const isCustomPreview = !ideaInfo;
    // Custom flow: build a route string from the destinations picked so
    // far in order, e.g. "Amboseli -> Maasai Mara", without repeating
    // the first stop at the end.
    const customRouteNames = Array.from(
      new Set(selectedDestinations.map((d) => d.name).filter(Boolean))
    );
    const customRoute =
      customRouteNames.length > 0
        ? customRouteNames.join(' -> ')
        : null;
    const previewRoute = customRoute || cleanRoute(ideaInfo?.route);

    // Classification is no longer a manual choice — it's derived live from
    // wherever the budget slider is sitting.
    const classification = classificationFor(pricePerPerson);

    // Dynamic total estimated budget calculation
    const totalPeople = (adults || 0) + (childrenCount || 0) || 1;
    const peopleParts = [];
    if (adults > 0) peopleParts.push(`${adults} adult${adults === 1 ? '' : 's'}`);
    if (childrenCount > 0) peopleParts.push(`${childrenCount} child${childrenCount === 1 ? '' : 'ren'}`);
    const peopleBreakdown = peopleParts.join(' + ') || '1 traveler';
    const activeNights = numberOfDays ? Math.max(numberOfDays - 1, 1) : 1;
    const approxTotal = pricePerPerson * totalPeople * activeNights;

    const handleInitialSubmit = (e) => {
      e?.preventDefault();

      let hasError = false;

      if (!pricePerPerson) {
        setBudgetTooltip('Please select your per-person budget.');
        hasError = true;
      } else {
        setBudgetTooltip('');
      }

      if (!fullName || !fullName.trim()) {
        setFullNameTooltip('Please enter your full name.');
        hasError = true;
      } else {
        setFullNameTooltip('');
      }

      if (!email || !email.trim()) {
        setEmailTooltip('Please enter your email address.');
        hasError = true;
      } else {
        setEmailTooltip('');
      }

      if (!phone || !phone.trim()) {
        setPhoneTooltip('Please enter your phone number.');
        hasError = true;
      } else {
        setPhoneTooltip('');
      }

      if (!residenceCountry || !residenceCountry.trim()) {
        setResidenceCountryTooltip('Please select your country of residence.');
        hasError = true;
      } else {
        setResidenceCountryTooltip('');
      }

      if (hasError) {
        setTimeout(() => {
          if (contentContainerRef.current) {
            const firstErrorEl = contentContainerRef.current.querySelector(
              `.${styles['has-error']}, .${styles['input-error']}`
            );
            if (firstErrorEl) {
              firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
              contentContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }
        }, 50);
        return;
      }

      setLoadingCode(true);
      setErrorMessage('');

      // Build a descriptive list of countries for the selected destinations
      const selectedCountryIds = Array.from(
        new Set(
          selectedDestinations
            .map(
              (d) => d?.country || d?.countryId || (d?.country && d.country._id)
            )
            .filter(Boolean)
        )
      );

      const selectedCountriesNames = selectedCountryIds
        .map((id) => {
          const found = countriesList.find((c) => c._id === id);
          return found ? found.name : id;
        })
        .filter(Boolean);

      const travelCountriesStr = selectedCountriesNames.length
        ? selectedCountriesNames.join(', ')
        : countriesList.find((item) => item._id === travelCountry)?.name ||
          'Not selected';

      const mergedSelected = [...thingsToDo];
      const selectedThingsFinal =
        mergedSelected.length > 0 ? mergedSelected.join(', ') : 'None';
      const finalComments = `Things to Do: ${selectedThingsFinal}, Special Requests: ${comments}`;

      const data = {
        startDate,
        numberOfDays,
        safariType,
        adults,
        children: childrenCount,
        infants,
        flexible: !!flexible,
        fullName,
        country: residenceCountry,
        comments: finalComments,
        emailAddress: email,
        phoneNumber: phone,
        classification,
        pricePerPerson,
        potentialRevenue: approxTotal,
        destinations: selectedDestinations.map((item) => item._id),
        travelCountries: travelCountriesStr,
        verified: true,
        selectedCountryIds,
        ...(propertyInfo?._id
          ? {
              propertyId: propertyInfo._id,
              subjectId: propertyInfo._id,
              subject: 'property',
              intent: propertyInfo.intent,
            }
          : {}),
      };

      api('post', 'safari/safari-enquiry', data)
        .then((res) => {
          setShowPlanner(false);
          setLoadingCode(false);
          if (propertyInfo?.slug) {
            navigate({
              to: `/accommodation/${propertyInfo.slug}`,
              search: {
                intent: propertyInfo.intent || 'quotes',
                ref: res.data.referenceNumber || '',
                enquiry: 'success',
              },
              state: {
                name: fullName || 'Traveller',
                justEnquired: true,
                operatorsIds: res.data.operatorsIds || [],
              },
            });
          } else {
            navigate({
              to: '/enquiry-confirmation',
              search: { ref: res.data.referenceNumber || '' },
              state: {
                name: fullName || 'Traveller',
                operatorsIds: res.data.operatorsIds || [],
              },
            });
          }
        })
        .catch((err) => {
          setErrorMessage(
            err?.response?.data?.message ||
              'Something went wrong. Please try again.'
          );
          setLoadingCode(false);
        });
    };

    const stepLabels = [
      '1. Trip & Activities',
      '2. Budget & Contact',
    ];

    const handleClose = () => {
      setShowPlanner(false);
      setCurrentStep(0);
      setErrorMessage('');
      setLoadingCode(false);
      setPropertyInfo(null);

      setStartDate('');
      setDateTooltip('');
      setNumberOfDays(null);
      setAdults(2);
      setChildrenCount(0);
      setInfants(0);
      setThingsToDo([]);
      setCustomThings('');
      setFlexible(null);
      setFlexibleTooltip('');
      setFullName('');
      setEmail('');
      setPhone('');
      setResidenceCountry('');
      setTravelCountry('');
      setDestinationsList([]);
      setSelectedDestinations([]);
      setIdeaInfo(null);
      setPendingRouteNames([]);
      setDestinationTooltip('');
      setDaysTooltip('');
      setBudgetTooltip('');
      setFullNameTooltip('');
      setEmailTooltip('');
      setPhoneTooltip('');
      setResidenceCountryTooltip('');
      setComments('');
      setPricePerPerson(null);
    };

    const handleThingToDoToggle = (item) => {
      setThingsToDo((prev) =>
        prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
      );
    };

    const handleDestinationToggle = (destination) => {
      setIdeaInfo(null);
      setSelectedDestinations((prev) => {
        const exists = prev.some((item) => item._id === destination._id);
        if (exists) {
          return prev.filter((item) => item._id !== destination._id);
        }
        setDestinationTooltip('');
        return [...prev, destination];
      });
    };

    const handleRemoveDestination = (destinationId) => {
      setIdeaInfo(null);
      setSelectedDestinations((prev) =>
        prev.filter((item) => item._id !== destinationId)
      );
    };

    const handleNext = (e) => {
      e?.preventDefault();
      e?.stopPropagation();

      let hasError = false;
      if (currentStep === 0 && selectedDestinations.length === 0) {
        setDestinationTooltip(
          'Please select at least one destination to continue.'
        );
        hasError = true;
      } else {
        setDestinationTooltip('');
      }
      if (currentStep === 0 && !startDate) {
        setDateTooltip('Please select your travel date to continue.');
        hasError = true;
      } else {
        setDateTooltip('');
      }
      if (currentStep === 0 && (!numberOfDays || numberOfDays < 2)) {
        setDaysTooltip('Please enter at least 2 days to continue.');
        hasError = true;
      } else {
        setDaysTooltip('');
      }
      if (currentStep === 0 && flexible === null) {
        setFlexibleTooltip('Please select whether you are flexible on dates.');
        hasError = true;
      } else {
        setFlexibleTooltip('');
      }

      if (hasError) {
        setTimeout(() => {
          if (contentContainerRef.current) {
            const firstErrorEl = contentContainerRef.current.querySelector(
              `.${styles['has-error']}, .${styles['input-error']}`
            );
            if (firstErrorEl) {
              firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
              contentContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }
        }, 50);
        return;
      }

      setBudgetTooltip('');
      setFullNameTooltip('');
      setEmailTooltip('');
      setPhoneTooltip('');
      setResidenceCountryTooltip('');
      setDateTooltip('');
      setErrorMessage('');

      setCurrentStep(1);
    };

    return (
      <>
        {hasStepsContent && (
          <section className={styles['extra-info-section']}>
            <div className={styles['container']}>
              {renderContent()}
              {renderSteps()}
              <div className={styles['how-cta']}>
                <button
                  className={styles['btn-plan-safari']}
                  onClick={() =>
                    onOpenPlanner
                      ? onOpenPlanner()
                      : (setShowPlanner(true), setCurrentStep(0))
                  }
                >
                  Design your Safari  →
                </button>
                {onBrowseCamps && (
                  <button
                    className={styles['btn-outline-dark']}
                    onClick={onBrowseCamps}
                  >
                    See Where You Can Stay →
                  </button>
                )}
              </div>
            </div>
          </section>
        )}
        {showPlanner &&
          portalMounted &&
          portalRootRef.current &&
          createPortal(
            <div className={styles['planner-overlay']}>
              <div className={styles['planner-modal']}>
                {/* 2-Step Stepper Header */}
                <div className={styles['step-labels']}>
                  <span
                    className={`${styles['step-label']} ${currentStep === 0 ? styles['active'] : ''}`}
                  >
                    1. Trip &amp; Activities
                  </span>
                  <span
                    className={`${styles['step-label']} ${currentStep === 1 ? styles['active'] : ''}`}
                  >
                    2. Budget &amp; Contact
                  </span>
                </div>
                <div className={styles['stepper']}>
                  <div
                    className={`${styles['step-dot']} ${currentStep === 0 ? styles['active'] : styles['done']}`}
                  >
                    1
                  </div>
                  <div className={styles['step-track']}>
                    <div
                      className={styles['step-track-fill']}
                      style={{ width: currentStep === 1 ? '100%' : '0%' }}
                    />
                  </div>
                  <div
                    className={`${styles['step-dot']} ${currentStep === 1 ? styles['active'] : ''}`}
                  >
                    2
                  </div>
                </div>

                {currentStep === 1 && (
                  <div className={styles['step-header-row']}>
                    <div className={styles['step-header-text']}>
                      <h1 className={styles['planner-title']}>
                        Almost there — budget &amp; contact details.
                      </h1>
                      <p className={styles['planner-subtitle']}>
                        Set your budget and tell us where to send your itinerary.
                      </p>
                    </div>
                    {safariType && (
                      <div className={styles['safari-type-tag']}>{safariType}</div>
                    )}
                  </div>
                )}


                {propertyInfo && (
                  <div
                    style={{
                      background: '#f4eee1',
                      padding: '0.8rem 1.4rem',
                      borderRadius: '6px',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '1.2rem',
                      color: '#211c14',
                      marginBottom: '1.5rem',
                      borderLeft: '4px solid #bf8a2e',
                      fontWeight: 500,
                    }}
                  >
                    Enquiring for: <strong>{propertyInfo.title}</strong>{' '}
                    ({propertyInfo.intent === 'book' ? 'Book Direct' : 'Book Now'})
                  </div>
                )}

                {/* Step Content */}
                <div
                  ref={contentContainerRef}
                  className={styles['planner-step-content-container']}
                  onScroll={checkScrollPosition}
                >
                  {/* STEP 1: Trip & Activities */}
                  {currentStep === 0 && (
                    <div className={styles['step0-layout']}>
                      <div className={styles['step-pane']}>
                        <h1 className={styles['planner-title']}>
                          Ready? Safaris are life changing, so get ready for a wild one.
                        </h1>
                        <p className={styles['planner-subtitle']}>
                          Tell us where you want to go and what you'd love to do — one page, two minutes.
                        </p>

                        {/* Country */}
                        <div className={styles['form-section']}>
                          <label className={styles['field-label']}>
                            Where would you like to travel?
                          </label>
                          <select
                            value={travelCountry}
                            onChange={(e) => setTravelCountry(e.target.value)}
                            className={styles['planner-input']}
                          >
                            <option value="">Choose a country</option>
                            {countriesList.map((countryItem) => (
                              <option
                                key={countryItem._id}
                                value={countryItem._id}
                              >
                                {countryItem.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Select Destinations */}
                        <div className={`${styles['form-section']} ${destinationTooltip ? styles['has-error'] : ''}`}>
                          <label className={styles['field-label']}>
                            Select destinations
                          </label>
                          {travelCountry ? (
                            <div className={styles['chip-grid']}>
                              {destinationsList.length > 0 ? (
                                destinationsList.map((dest) => {
                                  const selected = selectedDestinations.some(
                                    (item) => item._id === dest._id
                                  );
                                  const subtext =
                                    dest.heroSubtext ||
                                    dest.subtext ||
                                    dest.tagLine ||
                                    (dest._id === destination?._id ? destination?.heroSubtext : null);
                                  const tooltipId = `dest-tip-${dest._id}`;
                                  return (
                                    <React.Fragment key={dest._id}>
                                      <button
                                        type="button"
                                        className={`${styles['chip']} ${selected ? styles['selected'] : ''}`}
                                        onClick={() => handleDestinationToggle(dest)}
                                        {...(subtext
                                          ? {
                                              'data-tooltip-id': tooltipId,
                                              'data-tooltip-content': subtext,
                                            }
                                          : {})}
                                      >
                                        {selected && (
                                          <span className={styles['check']}>✓</span>
                                        )}
                                        {dest.name}
                                      </button>
                                      {subtext && (
                                        <Tooltip
                                          id={tooltipId}
                                          className={styles['dest-tooltip']}
                                          place="top"
                                          delayShow={150}
                                        />
                                      )}
                                    </React.Fragment>
                                  );
                                })
                              ) : (
                                <p className={styles['destination-note']}>
                                  Loading destinations for this country or no destinations available.
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className={styles['destination-note']}>
                              Choose a country to view its available destinations.
                            </p>
                          )}
                          {destinationTooltip && (
                            <p className={styles['planner-tooltip']}>
                              {destinationTooltip}
                            </p>
                          )}
                        </div>

                        {/* Approx date + Number of days */}
                        <div className={`${styles['form-section']} ${styles['row']}`}>
                          <div className={dateTooltip ? styles['has-error'] : ''}>
                            <label className={styles['field-label']}>
                              Travel date
                            </label>
                            <input
                              type="date"
                              value={startDate || ''}
                              min={
                                new Date(Date.now() + 86400000)
                                  .toISOString()
                                  .split('T')[0]
                              }
                              onChange={(e) => {
                                setStartDate(e.target.value);
                                setDateTooltip('');
                              }}
                              className={`${styles['planner-input']} ${dateTooltip ? styles['input-error'] : ''}`}
                            />
                            {dateTooltip && (
                              <p className={styles['planner-tooltip']}>
                                {dateTooltip}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className={styles['field-label']}>
                              Number of days
                            </label>
                            <input
                              type="number"
                              value={numberOfDays || ''}
                              min={2}
                              placeholder=""
                              onChange={(e) => {
                                setNumberOfDays(
                                  e.target.value ? Number(e.target.value) : null
                                );
                                setDaysTooltip('');
                                setIdeaInfo(null);
                              }}
                              className={`${styles['planner-input']} ${daysTooltip ? styles['input-error'] : ''}`}
                            />
                            {daysTooltip && (
                              <p className={styles['planner-tooltip']}>
                                {daysTooltip}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Flexible on dates */}
                        <div className={`${styles['form-section']} ${styles['form-section--inline']} ${flexibleTooltip ? styles['has-error'] : ''}`}>
                          <label className={styles['field-label']}>
                            Are you flexible on dates?
                          </label>
                          <div className={styles['radio-group']}>
                            <button
                              type="button"
                              className={`${styles['radio-chip']} ${flexible === true ? styles['selected'] : ''} ${flexibleTooltip ? styles['radio-chip-error'] : ''}`}
                              onClick={() => {
                                setFlexible(true);
                                setFlexibleTooltip('');
                              }}
                            >
                              <span className={styles['radio-dot']}>
                                <span className={styles['radio-dot-inner']} />
                              </span>
                              Yes
                            </button>
                            <button
                              type="button"
                              className={`${styles['radio-chip']} ${flexible === false ? styles['selected'] : ''} ${flexibleTooltip ? styles['radio-chip-error'] : ''}`}
                              onClick={() => {
                                setFlexible(false);
                                setFlexibleTooltip('');
                              }}
                            >
                              <span className={styles['radio-dot']}>
                                <span className={styles['radio-dot-inner']} />
                              </span>
                              No
                            </button>
                          </div>
                          {flexibleTooltip && (
                            <p className={styles['planner-tooltip']} style={{ width: '100%' }}>
                              {flexibleTooltip}
                            </p>
                          )}
                        </div>

                        {/* What would you like to see and do? */}
                        <div className={styles['form-section']}>
                          <label className={styles['field-label']}>
                            What would you like to see and do?
                          </label>
                          <div className={styles['chip-grid']}>
                            {[
                              'Game drives',
                              'See the Wildebeest Migration',
                              'See the Culture',
                              'Do a Hot Air Balloon Safari',
                              'Have a few days on the beach',
                              'Go Deep Sea Fishing',
                              'Special Trip',
                              'Anniversary/Birthday/Honeymoon',
                            ].map((activity) => {
                              const selected = thingsToDo.includes(activity);
                              return (
                                <button
                                  key={activity}
                                  type="button"
                                  className={`${styles['chip']} ${selected ? styles['selected'] : ''}`}
                                  onClick={() => handleThingToDoToggle(activity)}
                                >
                                  {selected && (
                                    <span className={styles['check']}>✓</span>
                                  )}
                                  {activity}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* More things you want to do */}
                        <div className={styles['form-section']}>
                          <label className={styles['field-label']}>
                            More things you want to do
                          </label>
                          <textarea
                            placeholder="Type any other activities or ideas here..."
                            value={customThings}
                            onChange={(e) => setCustomThings(e.target.value)}
                            onBlur={() => {
                              const items = (customThings || '')
                                .split(/[,\n]/)
                                .map((s) => s.trim())
                                .filter(Boolean);
                              if (items.length > 0) {
                                setThingsToDo((prev) => {
                                  const merged = [...prev];
                                  items.forEach((it) => {
                                    if (!merged.includes(it)) merged.push(it);
                                  });
                                  return merged;
                                });
                              }
                            }}
                            className={styles['planner-textarea']}
                            rows={3}
                          />
                        </div>


                      </div>

                      {/* Visual preview card (retained on right) */}
                      <div
                        className={`${styles['trip-preview']} ${isCustomPreview ? styles['trip-preview--custom'] : ''}`}
                      >
                        <div
                          className={styles['trip-preview-media']}
                          style={
                            previewImage
                              ? { backgroundImage: `url(${previewImage})` }
                              : undefined
                          }
                        >
                          <div className={styles['trip-preview-overlay']}>
                            {previewDuration && (
                              <span className={styles['trip-preview-days']}>
                                {previewDuration}
                              </span>
                            )}
                            <h3 className={styles['trip-preview-title']}>
                              {previewTitle}
                            </h3>
                            <div className={styles['trip-preview-route']}>
                              {previewRoute ||
                                (isCustomPreview
                                  ? 'Pick a destination to start building it'
                                  : null)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: Budget & Contact */}
                  {currentStep === 1 && (
                    <div className={styles['step-pane']}>

                      {/* Who is traveling? */}
                      <div className={styles['form-section']}>
                        <label className={styles['field-label']}>
                          Who is traveling?
                        </label>
                        <div className={styles['guest-row']}>
                          <div className={styles['counter-box']}>
                            <div className={styles['counter-label']}>Adults</div>
                            <div className={styles['counter-controls']}>
                              <button
                                type="button"
                                className={styles['round-btn']}
                                onClick={() =>
                                  setAdults((prev) => Math.max(1, prev - 1))
                                }
                              >
                                –
                              </button>
                              <span className={styles['counter-value']}>
                                {adults}
                              </span>
                              <button
                                type="button"
                                className={styles['round-btn']}
                                onClick={() => setAdults((prev) => prev + 1)}
                              >
                                +
                              </button>
                            </div>
                          </div>

                          <div className={styles['counter-box']}>
                            <div>
                              <div className={styles['counter-label']}>Children</div>
                              <div className={styles['counter-sub']}>2–12 yrs</div>
                            </div>
                            <div className={styles['counter-controls']}>
                              <button
                                type="button"
                                className={styles['round-btn']}
                                onClick={() =>
                                  setChildrenCount((prev) => Math.max(0, prev - 1))
                                }
                              >
                                –
                              </button>
                              <span className={styles['counter-value']}>
                                {childrenCount}
                              </span>
                              <button
                                type="button"
                                className={styles['round-btn']}
                                onClick={() =>
                                  setChildrenCount((prev) => prev + 1)
                                }
                              >
                                +
                              </button>
                            </div>
                          </div>

                          <div className={styles['counter-box']}>
                            <div>
                              <div className={styles['counter-label']}>Infants</div>
                              <div className={styles['counter-sub']}>&lt;2 yrs</div>
                            </div>
                            <div className={styles['counter-controls']}>
                              <button
                                type="button"
                                className={styles['round-btn']}
                                onClick={() =>
                                  setInfants((prev) => Math.max(0, prev - 1))
                                }
                              >
                                –
                              </button>
                              <span className={styles['counter-value']}>
                                {infants}
                              </span>
                              <button
                                type="button"
                                className={styles['round-btn']}
                                onClick={() =>
                                  setInfants((prev) => prev + 1)
                                }
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Budget */}
                      <div className={`${styles['form-section']} ${budgetTooltip ? styles['has-error'] : ''}`}>
                        <label className={styles['field-label']}>
                          What is your per-person budget? *
                        </label>
                        <div className={styles['budget-display']}>
                          {pricePerPerson ? (
                            <>
                              <span>${pricePerPerson}</span>
                              <PppnsTooltip className={styles['budget-unit']} />
                            </>
                          ) : (
                            <span style={{ fontSize: '2.2rem', color: '#8a621f', fontFamily: "'Inter', sans-serif" }}>
                              Slide to set budget
                            </span>
                          )}
                        </div>
                        <input
                          type="range"
                          min={BUDGET_MIN}
                          max={BUDGET_MAX}
                          step={BUDGET_STEP}
                          value={pricePerPerson || BUDGET_MIN}
                          onChange={(e) => {
                            setPricePerPerson(Number(e.target.value));
                            setBudgetTooltip('');
                          }}
                          className={`${styles['budget-slider-input']} ${budgetTooltip ? styles['input-error'] : ''}`}
                        />
                        <div className={styles['range-labels']}>
                          <span>${BUDGET_MIN}</span>
                          <span>${BUDGET_MAX}+</span>
                        </div>
                        {budgetTooltip && (
                          <p className={styles['planner-tooltip']}>
                            {budgetTooltip}
                          </p>
                        )}
                        <div className={styles['budget-summary-row']}>
                          <span
                            className={`${styles['budget-tag']} ${
                              classification === 'Luxury'
                                ? styles['budget-tag--luxury']
                                : ''
                            }`}
                          >
                            {pricePerPerson ? (
                              <>
                                ${pricePerPerson.toLocaleString()} pppns × {totalPeople}{' '}
                                {totalPeople === 1 ? 'person' : 'people'} ({peopleBreakdown}) ×{' '}
                                {activeNights} {activeNights === 1 ? 'night' : 'nights'} ={' '}
                                <strong className={styles['budget-total-highlight']}>
                                  ~${approxTotal.toLocaleString()}
                                </strong>
                              </>
                            ) : (
                              'Move slider to select your per-person budget'
                            )}
                          </span>

                          {pricePerPerson ? (
                            <div className={styles['budget-exclusions-note']}>
                              <span className={styles['budget-exclusions-icon']}>i</span>
                              <div className={styles['budget-exclusions-text']}>
                                <strong>Please note:</strong> This estimate value does not include <strong>1. Park / Conservancy fees</strong> or <strong>2. Transport</strong>. The operator responding to your enquiry will provide detailed information on these extra fees.
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Contact form */}
                      <form
                        id="safari-contact-form"
                        onSubmit={handleInitialSubmit}
                        className={styles['contact-form']}
                      >
                        {errorMessage && (
                          <div className={styles['error-message-banner']}>
                            {errorMessage}
                          </div>
                        )}

                        <div className={`${styles['form-section']} ${styles['row']}`}>
                          <div className={fullNameTooltip ? styles['has-error'] : ''}>
                            <label className={styles['field-label']}>
                              Full name *
                            </label>
                            <input
                              type="text"
                              placeholder="John Doe"
                              value={fullName}
                              onChange={(e) => {
                                setFullName(e.target.value);
                                setFullNameTooltip('');
                              }}
                              className={`${styles['planner-input']} ${fullNameTooltip ? styles['input-error'] : ''}`}
                            />
                            {fullNameTooltip && (
                              <p className={styles['planner-tooltip']}>
                                {fullNameTooltip}
                              </p>
                            )}
                          </div>
                          <div className={emailTooltip ? styles['has-error'] : ''}>
                            <label className={styles['field-label']}>
                              Email address *
                            </label>
                            <input
                              type="email"
                              placeholder="john@example.com"
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setEmailTooltip('');
                              }}
                              className={`${styles['planner-input']} ${emailTooltip ? styles['input-error'] : ''}`}
                            />
                            {emailTooltip && (
                              <p className={styles['planner-tooltip']}>
                                {emailTooltip}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className={`${styles['form-section']} ${styles['row']}`}>
                          <div className={phoneTooltip ? styles['has-error'] : ''}>
                            <label className={styles['field-label']}>
                              Phone number *
                            </label>
                            <input
                              type="tel"
                              placeholder="+254..."
                              value={phone}
                              onChange={(e) => {
                                setPhone(e.target.value);
                                setPhoneTooltip('');
                              }}
                              className={`${styles['planner-input']} ${phoneTooltip ? styles['input-error'] : ''}`}
                            />
                            {phoneTooltip && (
                              <p className={styles['planner-tooltip']}>
                                {phoneTooltip}
                              </p>
                            )}
                          </div>
                          <div className={residenceCountryTooltip ? styles['has-error'] : ''}>
                            <label className={styles['field-label']}>
                              Country of residence *
                            </label>
                            <CountrySelect
                              name="country"
                              value={residenceCountry}
                              setFieldValue={(name, val) => {
                                setResidenceCountry(val);
                                setResidenceCountryTooltip('');
                              }}
                              setFieldTouched={() => {}}
                            />
                            {residenceCountryTooltip && (
                              <p className={styles['planner-tooltip']}>
                                {residenceCountryTooltip}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className={styles['form-section']}>
                          <label className={styles['field-label']}>
                            Special requests / comments
                          </label>
                          <textarea
                            placeholder="Dietary requirements, accessibility, target animals, etc."
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            className={styles['planner-textarea']}
                            rows={3}
                          />
                        </div>

                        <p className={styles['required-note']}>
                          * Required fields
                        </p>
                      </form>
                    </div>
                  )}
                </div>

                {canScrollDown && (
                  <button
                    type="button"
                    className={styles['scroll-more-indicator']}
                    onClick={() => {
                      if (contentContainerRef.current) {
                        contentContainerRef.current.scrollBy({ top: 220, behavior: 'smooth' });
                      }
                    }}
                  >
                    Scroll for more options ↓
                  </button>
                )}

                {/* Footer Navigation */}
                <div className={styles['planner-actions']}>
                  {currentStep === 0 ? (
                    <React.Fragment key="step-0-actions">
                      <button
                        type="button"
                        className={styles['btn-ghost']}
                        onClick={handleClose}
                      >
                        Cancel
                      </button>
                      <button
                        key="btn-next-step"
                        type="button"
                        className={styles['btn-primary']}
                        onClick={handleNext}
                      >
                        Next: Budget &amp; Contact →
                      </button>
                    </React.Fragment>
                  ) : (
                    <React.Fragment key="step-1-actions">
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          type="button"
                          className={styles['btn-default']}
                          onClick={() => setCurrentStep(0)}
                        >
                          ← Back
                        </button>
                        <button
                          type="button"
                          className={styles['btn-ghost']}
                          onClick={handleClose}
                        >
                          Cancel
                        </button>
                      </div>
                      <button
                        key="btn-submit-enquiry"
                        type="submit"
                        form="safari-contact-form"
                        disabled={loadingCode}
                        className={styles['btn-primary']}
                      >
                        {loadingCode
                          ? 'SUBMITTING...'
                          : 'SUBMIT SAFARI ENQUIRY'}
                      </button>
                    </React.Fragment>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
      </>
    );
  }
);

export default ExtraInformation;
